import { type AssetVizController, AssetVizMode } from '@asset-viz/defines'
import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { ForecastAssetVizWindow } from 'features/assetVizAdapter/ForecastAssetVizWindow'
import { useCdfRawValues } from 'features/dataLayers'
import { useInspectionLifeLayer } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useInspectionLifeLayer'
import { useInspectionViewSelection } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useInspectionViewSelection'
import {
  type InspectionLayerKey,
  inspectionLayerLabels,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionLayers'
import type {
  InspectionResult,
  InspectionViewContext,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import { inspectionProvenance } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import { inspectionThicknessSource } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionThicknessSource'
import { useGeckoLocale } from 'features/locale'
import { useRolodexEntries } from 'features/rolodex'
import isNil from 'lodash/isNil'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentRect,
  GeckoMeasurement,
  isInspectionThicknessCDF,
  RolodexEntryType,
} from 'types'

import { InspectionFindingActions } from './InspectionFindingActions'

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`
const LayerSelect = styled.select`
  padding: 0.4rem 0.6rem;
  color: inherit;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 0.375rem;
`

const LifecycleViewport = styled(ForecastAssetVizWindow)`
  width: 100%;
  height: 100%;
  min-height: 0;
`

const Viewport = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 22rem;
  height: 55vh;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--background);
`

export default function DocChatInspectionViewer({
  result,
  assetModelId,
  inspectionId,
  datasetId,
  componentId,
  unitId,
  siteId,
  onViewContextChange,
}: {
  result: InspectionResult
  inspectionId: string
  datasetId: string
  assetModelId: string
  componentId: string
  unitId: string
  siteId: string
  onViewContextChange?: (context: InspectionViewContext | null) => void
}) {
  const {
    assetModels,
    components,
    inspections,
    isLoading,
    hasEverLoaded,
    error,
  } = useRolodexEntries({
    ids: [assetModelId, componentId, inspectionId],
    type: [
      RolodexEntryType.ASSET_MODEL,
      RolodexEntryType.COMPONENT,
      RolodexEntryType.INSPECTION,
    ],
    tags: { unit: unitId, site: siteId },
  })
  const {
    componentDataFrames,
    componentDatasets,
    isLoading: dataLoading,
    hasEverLoaded: dataLoaded,
    error: dataError,
  } = useRolodexEntries({
    ids: [datasetId],
    type: RolodexEntryType.COMPONENT_DATASET,
    tags: {
      component: componentId,
      inspection: inspectionId,
      site: siteId,
      unit: unitId,
    },
  })
  const viewer = useRef<AssetVizController | null>(null)
  const hadFocus = useRef(false)
  const source = inspectionProvenance(result)
  const dataset = componentDatasets.find((entry) => entry.id === datasetId)
  const sourceChanged =
    !isNil(source.dataset_generation) &&
    !isNil(dataset) &&
    dataset.generation !== source.dataset_generation
  const thicknessCDF = sourceChanged
    ? undefined
    : inspectionThicknessSource(
        componentDataFrames.filter(isInspectionThicknessCDF),
        datasetId,
        source.column ?? undefined
      )
  const assetModel = assetModels.find((model) => model.id === assetModelId)
  const component = components.find((entry) => entry.id === componentId)
  const inspection = inspections.find((entry) => entry.id === inspectionId)
  const { t, i18n } = useLingui()
  const [layerKey, setLayerKey] = useState<InspectionLayerKey>(
    result.presentation?.layer ??
      (result.kind === 'remaining_life' ? 'remaining_life' : 'thickness')
  )
  const { layer, failed, availableLayers } = useInspectionLifeLayer(
    result,
    assetModel,
    thicknessCDF,
    inspection,
    layerKey
  )
  const cdf = layerKey === 'thickness' ? thicknessCDF : layer
  const { buffer, isLoading: valuesLoading } = useCdfRawValues(cdf)
  const {
    focus,
    setFocus,
    activeBounds,
    extreme,
    focusRect,
    handleFinding,
    handleSelected,
  } = useInspectionViewSelection(
    result,
    cdf,
    buffer?.values,
    componentId,
    setLayerKey
  )
  const cameraFocus = useMemo(() => {
    if (!cdf || !focusRect) return undefined
    const dx = cdf.data.binning.bin_size.width.scale(20)
    const dy = cdf.data.binning.bin_size.height.scale(20)
    return GeckoComponentRect.CreateFromXYs(
      componentId,
      focusRect.start.x.minus(dx).num,
      focusRect.start.y.minus(dy).num,
      focusRect.end.x.plus(dx).num,
      focusRect.end.y.plus(dy).num,
      focusRect.start.x.unit,
      focusRect.start.y.unit
    )
  }, [cdf, componentId, focusRect])
  useEffect(() => {
    if (cameraFocus) {
      hadFocus.current = true
    } else if (hadFocus.current) {
      hadFocus.current = false
      viewer.current?.cancelZoom()
      void viewer.current?.resetToInitialView({ animated: true })
    }
  }, [cameraFocus])
  const { formatMeasurement } = useGeckoLocale()
  useEffect(() => {
    onViewContextChange?.(
      cdf &&
        assetModel &&
        component &&
        inspection &&
        !isLoading &&
        !dataLoading &&
        !error &&
        !dataError &&
        (isNil(source.asset_model_generation) ||
          assetModel.generation === source.asset_model_generation) &&
        (focus === 'whole' || activeBounds) &&
        (!activeBounds || focusRect)
        ? {
            site_id: siteId,
            unit_id: unitId,
            component_id: componentId,
            inspection_id: inspectionId,
            dataset_id: datasetId,
            asset_model_id: assetModelId,
            dataset_generation: source.dataset_generation ?? undefined,
            asset_model_generation: source.asset_model_generation ?? undefined,
            column: source.column ?? undefined,
            layer: layerKey,
            bin_bounds: activeBounds ?? null,
            as_of_date: source.as_of_date?.slice(0, 10),
          }
        : null
    )
  }, [
    onViewContextChange,
    assetModel,
    component,
    inspection,
    isLoading,
    dataLoading,
    focus,
    cdf,
    error,
    dataError,
    focusRect,
    activeBounds,
    siteId,
    unitId,
    componentId,
    inspectionId,
    datasetId,
    assetModelId,
    source,
    layerKey,
  ])
  useEffect(() => () => onViewContextChange?.(null), [onViewContextChange])

  if (
    error ||
    (hasEverLoaded && (!assetModel || !component)) ||
    (assetModel &&
      !isNil(source.asset_model_generation) &&
      assetModel.generation !== source.asset_model_generation)
  ) {
    return (
      <Text as='p'>
        <Trans>The inspection model is unavailable.</Trans>
      </Text>
    )
  }
  if (isLoading || !assetModel || !component) {
    return (
      <Text as='p' role='status'>
        <Trans>Loading inspection model…</Trans>
      </Text>
    )
  }

  if (dataError || (dataLoaded && !thicknessCDF)) {
    return (
      <Text as='p'>
        <Trans>Inspection heatmap data is unavailable.</Trans>
      </Text>
    )
  }
  if (dataLoading || !thicknessCDF) {
    return (
      <Text as='p' role='status'>
        <Trans>Loading inspection data…</Trans>
      </Text>
    )
  }

  return (
    <>
      {result.findings?.length ? (
        <InspectionFindingActions
          findings={result.findings}
          limit={100}
          onSelect={handleFinding}
        />
      ) : null}
      <Controls>
        <LayerSelect
          aria-label={t`Inspection layer`}
          value={layerKey}
          onChange={(event) => {
            const key = availableLayers.find(
              (item) => item === event.target.value
            )
            if (key) setLayerKey(key)
          }}>
          {!availableLayers.includes(layerKey) ? (
            <option value={layerKey}>
              {i18n._(inspectionLayerLabels[layerKey])}
            </option>
          ) : null}
          {availableLayers.map((key) => (
            <option key={key} value={key}>
              {i18n._(inspectionLayerLabels[key])}
            </option>
          ))}
        </LayerSelect>
        <Button variant='outline' size='sm' onClick={() => setFocus('minimum')}>
          <Trans>Minimum</Trans>
        </Button>
        <Button variant='outline' size='sm' onClick={() => setFocus('maximum')}>
          <Trans>Maximum</Trans>
        </Button>
        <Button variant='ghost' size='sm' onClick={() => setFocus('whole')}>
          <Trans>Reset view</Trans>
        </Button>
      </Controls>
      {failed ? (
        <Text as='p' variant='caption'>
          <Trans>
            This layer is unavailable for the selected inspection inputs.
          </Trans>
        </Text>
      ) : null}
      {!cdf && !failed ? (
        <Text role='status'>
          <Trans>Loading inspection data…</Trans>
        </Text>
      ) : null}
      {cdf ? (
        <>
          <Viewport>
            <LifecycleViewport
              ref={viewer}
              key={`${assetModelId}-${inspectionId}-${datasetId}`}
              focusZoomRect={cameraFocus}
              selection={focusRect}
              assetModel={assetModel}
              mode={AssetVizMode.SelectBin}
              components={[component]}
              componentDataFrames={[cdf]}
              selectedCDF={cdf}
              selectedInspection={inspection}
              showResolutionSelector={false}
              initialShow3D={result.presentation?.mode !== '2D'}
              expandedTitle={t`Inspection`}
              onSelected={handleSelected}
              sceneConfig={{
                showFeatures: result.presentation?.show_features ?? true,
                showFeatureLabels: true,
                showRulers: true,
                showCompass: false,
                showLegend: true,
              }}
            />
          </Viewport>
          {extreme && !valuesLoading ? (
            <Text variant='caption'>
              {focus === 'minimum' ? (
                <Trans>Minimum</Trans>
              ) : (
                <Trans>Maximum</Trans>
              )}
              :{' '}
              {formatMeasurement({
                measurement: GeckoMeasurement.CreateFromValue(
                  extreme.value,
                  cdf.data.params.measurement_unit
                ),
              })}
            </Text>
          ) : null}
        </>
      ) : null}
      <Text as='p' variant='caption' color='muted'>
        <Trans>
          Drag to rotate. Scroll to zoom. Hover to inspect readings. Uncolored
          areas have no displayed reading.
        </Trans>
      </Text>
    </>
  )
}
